import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Result } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import AiccTheme from './AiccTheme'
import { canAccessAiccRoute } from './aicc-navigation.mjs'

type BoundaryProps = {
  children: ReactNode
  onReturn: () => void
  resetKey: string
}

type BoundaryState = {
  failed: boolean
}

class AiccRouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false }

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true }
  }

  componentDidUpdate(previousProps: BoundaryProps) {
    if (this.state.failed && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false })
    }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('[llmbot-next] AICC route failed', {
      errorName: error instanceof Error ? error.name : 'UnknownError',
      componentStack: info.componentStack,
    })
  }

  render() {
    if (!this.state.failed) return this.props.children

    return (
      <Result
        status="error"
        title="Voice Operations page failed to load"
        subTitle="Other features still work. Go back to Bots and try again."
        extra={<Button type="primary" onClick={this.props.onReturn}>Back to Bots</Button>}
      />
    )
  }
}

export default function AiccRouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const profile = useSelector((state: any) => state.user.userInfo)

  if (!canAccessAiccRoute(location.pathname, profile)) {
    return <AiccTheme><Result status="403" title="This account is not authorized" subTitle="The page is outside this account's business line, role, or data scope." extra={<Button type="primary" onClick={() => navigate('/bots')}>Back to Bots</Button>} /></AiccTheme>
  }

  return (
    <AiccTheme>
      <AiccRouteErrorBoundary
        resetKey={location.pathname}
        onReturn={() => navigate('/bots')}
      >
        {children}
      </AiccRouteErrorBoundary>
    </AiccTheme>
  )
}
